import { latestAdConsent } from '~/composables/useAdConsent';
import { createPixelAccountSync, usePixelAccountApi } from '~/composables/useMetaPixelAccount';
import {
   flushHeldPixelEvents,
   identifyPixelUser,
   notePixelPageView,
   pixelIsEnabled,
   setPixelRouteParams,
} from '~/composables/useMetaPixel';
import { getClientPromptSessionId } from '~/utils/clientMobileAppPrompt';
import { clientAccountKey, isImpersonatingClientToken } from '~/utils/clientToken';

// Wires the Meta Pixel to the app: the signed-in consent check, the address the
// pixel may fire on, and the conversions the API offers. With no pixel id in the
// runtime config nothing below is set up. The signed-out consent decision is made
// elsewhere and reaches the pixel through setPixelAllowed.
export default defineNuxtPlugin({
   name: 'meta-pixel',
   dependsOn: ['acquisition-entry'],
   setup() {
      if (!pixelIsEnabled()) return;
      const { clientToken } = useAuthClientStorage();
      const { clientProfile } = storeToRefs(useClientInfo());
      const account = createPixelAccountSync(usePixelAccountApi());

      const accountId = (token?: string | null) => {
         const key = getClientPromptSessionId(token);
         return key ? key.slice('client:'.length) : null;
      };

      // A profile left over from another account must not lend its email to this one.
      const profileEmail = (id: string | null) =>
         id && String(clientProfile.value?.id) === id ? clientProfile.value?.email : undefined;

      watch(
         () => clientToken.value?.access_token,
         (token) => {
            const id = accountId(token);
            void account.sync({
               accountKey: clientAccountKey(token),
               accountId: id,
               email: profileEmail(id),
               impersonating: isImpersonatingClientToken(token),
            });
         },
         { immediate: true, flush: 'sync' },
      );

      // The profile usually arrives after the token; matching uses it when it does.
      watch(
         () => [clientProfile.value?.id, clientProfile.value?.email],
         () => {
            const id = accountId(clientToken.value?.access_token);
            const email = profileEmail(id);
            if (email && id && !isImpersonatingClientToken(clientToken.value?.access_token)) {
               void identifyPixelUser(email, id);
            }
         },
      );

      // The popup and the settings switch publish every answer the server gives.
      watch(latestAdConsent, (consent) => {
         if (consent) void account.consentChanged(consent);
      });

      // The first load is a navigation too. One that was redirected reaches here
      // once, for where it ended; one that failed left the page where it was.
      useRouter().afterEach((to, from, failure) => {
         setPixelRouteParams(Object.keys(to.params).length > 0);
         if (!failure) notePixelPageView(to.path === from.path);
         flushHeldPixelEvents();
         void account.refreshPending();
      });
   },
});
